import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetReportsQuery, useUpdateReportStatusMutation } from '../../services/portal';
import DataGrid from '../../components/Users/DataGrid';
import { Modal, Button, Tab, Tabs } from 'react-bootstrap';
import { getCurrentUser, isAdminOrManager } from '../../utils/authUtils';

const Reports = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const canManageReports = isAdminOrManager();

  const { data: reports, refetch: refetchReports } = useGetReportsQuery({ forceRefetch: true });
  const [updateReportStatus] = useUpdateReportStatusMutation();

  const [selectedReportId, setSelectedReportId] = useState(0);
  const [selectedReportName, setSelectedReportName] = useState('');
  const [selectedReportStatus, setSelectedReportStatus] = useState(false);

  const [reportModalShow, setReportModalShow] = useState(false);

  // Dropdown filter states
  const [filterOrg, setFilterOrg] = useState('');
  const [filterWorkspace, setFilterWorkspace] = useState('');
  const [filterReport, setFilterReport] = useState('');

  const handleNewReport = () => navigate('/reports/new');
  const handleEditReport = (reportId) => navigate(`/reports/${reportId.report_id}`);

  const handleReportModalShow = (report) => {
    setSelectedReportId(report.report_id);
    setSelectedReportName(report.report_name);
    setSelectedReportStatus(report.active);
    setReportModalShow(true);
  };

  const handleReportModalCancel = () => setReportModalShow(false);

  const handleSaveReportStatus = () => {
    const status = !selectedReportStatus;
    updateReportStatus({ report_id: selectedReportId, report_active: status })
      .unwrap()
      .then(() => refetchReports())
      .catch((err) => console.error('Failed to update report status:', err));
    setReportModalShow(false);
  };

  const handleNotification = (report) => {
    navigate(`/notifications/subscriptions/reports/${report.report_type}/${report.report_id}/${report.report_code}/${report.report_name}`);
  };

  const handleRights = (report) => {
    navigate(`/Reports/UserAssignment/ReportsGrid/${report.report_id}/${report.report_name}`);
  };

  const columns = [
    { field: 'organization.organization_name', header: 'Organization' },
    { field: 'workspace.workspace_name', header: 'Workspace' },
    { field: 'report_name', header: 'Name' },
    { field: 'report_code', header: 'Code' },
    { field: 'report_description', header: 'Description' },
  ];

  // Safely derive arrays
  const rows = (reports?.reports ?? []);

  // Unique dropdown values (guarded)
  const uniqueOrgs = [...new Set(rows.map(r => r.organization?.organization_name).filter(Boolean))];
  const uniqueWorkspaces = [...new Set(rows.map(r => r.workspace?.workspace_name).filter(Boolean))];
  const uniqueReports = [...new Set(rows.map(r => r.report_name).filter(Boolean))];

  // Apply filters
  const filtered = rows.filter(r =>
    (filterOrg === '' || r.organization?.organization_name === filterOrg) &&
    (filterWorkspace === '' || r.workspace?.workspace_name === filterWorkspace) &&
    (filterReport === '' || r.report_name === filterReport)
  );

  return (
    <div className="page-inner">
      <header className="page-title-bar">
        <div className="d-flex justify-content-between">
          <h1 className="page-title">
            <i className="fas fa-chart-pie text-muted mr-2"></i>Reports
          </h1>
          {canManageReports && (
            <div className="btn-toolbar">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNewReport}>
                + New
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Filters */}
      {/* Filters */}
{/* Filters */}
<div className="d-flex gap-4 mb-3">
  <div className="d-flex flex-column" style={{ margin: '10px' }}>
    <label className="form-label">Organization</label>
    <select
      className="custom-select"
      value={filterOrg}
      onChange={(e) => setFilterOrg(e.target.value)}
      style={{ minWidth: 200 }}
    >
      <option value="">All Organizations</option>
      {uniqueOrgs.map((org) => (
        <option key={org} value={org}>{org}</option>
      ))}
    </select>
  </div>

  <div className="d-flex flex-column" style={{ margin: '10px' }}>
    <label className="form-label">Workspace</label>
    <select
      className="custom-select"
      value={filterWorkspace}
      onChange={(e) => setFilterWorkspace(e.target.value)}
      style={{ minWidth: 200 }}
    >
      <option value="">All Workspaces</option>
      {uniqueWorkspaces.map((ws) => (
        <option key={ws} value={ws}>{ws}</option>
      ))}
    </select>
  </div>

  
</div>


      <div className="page-section">
        <Tabs defaultActiveKey="active" id="report-tabs">
          <Tab eventKey="active" title="Active">
            <div className="card p-3">
              <DataGrid
                data={filtered.filter(r => r.active)}
                columns={columns}
                onEditRow={canManageReports ? handleEditReport : undefined}
                onDeleteRow={canManageReports ? handleReportModalShow : undefined}
                onNotification={handleNotification}
                onRights={canManageReports ? handleRights : undefined}
                showDelete={canManageReports}
                showNotification={true}
                showEditRow={canManageReports}
                showRights={canManageReports}
              />
            </div>
          </Tab>
          <Tab eventKey="disabled" title="Disabled">
            <div className="card p-3">
              <DataGrid
                data={filtered.filter(r => !r.active)}
                columns={columns}
                onEditRow={canManageReports ? handleEditReport : undefined}
                onDeleteRow={canManageReports ? handleReportModalShow : undefined}
                onNotification={handleNotification}
                onRights={canManageReports ? handleRights : undefined}
                onActive={canManageReports ? handleReportModalShow : undefined}
                showDelete={false}
                showNotification={false}
                showEditRow={false}
                showRights={false}
                showActive={canManageReports}
              />
            </div>
          </Tab>
        </Tabs>
      </div>

      <Modal show={reportModalShow} onHide={handleReportModalCancel} animation={false} centered>
        <Modal.Header closeButton={false}>
          <Modal.Title>
            <h6>{selectedReportStatus ? 'Disable report' : 'Activate report'}</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {selectedReportStatus ? 'Disable' : 'Activate'} <strong>{selectedReportName}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleReportModalCancel}>Cancel</Button>
          <Button variant="warning" onClick={handleSaveReportStatus}>
            {selectedReportStatus ? 'Disable report' : 'Activate report'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Reports;
